import { useState } from "react";
import config from "config/config";
import { v4 as uuidv4 } from "uuid";
import { sha384 } from "crypto-hash";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import FormInputError from "components/General/FormInputError";
import { getUserProfile } from "service/userservice/getUserProfile";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import DomainErrorModal from "components/ModalDialogs/DomainErrorModal";

const StepTwo = ({
  agentRegistrationFormData,
  setAgentRegistrationFormData,
  setCurrentStep,
  setLoading,
  roleid,
}) => {
  const { t } = useTranslation();
  const uuid = uuidv4();
  const [errorInfo, seterrorInfo] = useState("");
  const [modalState, setModalState] = useState(false);
  const [openEyePassword, setOpenEyePassword] = useState(true);
  const [openEyeConfirmPassword, setOpenEyeConfirmPassword] = useState(true);
  const {
    register: registrationStepTwoRegister,
    getValues,
    handleSubmit,
    formState: { errors: registrationStepTwoErrors },
  } = useForm({
    defaultValues: agentRegistrationFormData,
  });

  async function onSubmit(data) {
    setLoading(true);
    let userProfile = await getUserProfile(data.username).catch((error) => {
      console.log(error);
    });

    setLoading(false);

    if (userProfile === null) {
      let hashedPassword = (await sha384(data.password))
        .toString()
        .substring(15, 30);
      data.hashedPassword = hashedPassword;
      setAgentRegistrationFormData((curr) => ({ ...curr, ...data }));
      setCurrentStep(3);
    } else {
      seterrorInfo(t("AgentRegister-step2-onSubmitUsernameError"));
      setModalState(true);
    }
  }

  const togglePasswordEye = () => {
    setOpenEyePassword(!openEyePassword);
  };

  const toggleConfirmPasswordEye = () => {
    setOpenEyeConfirmPassword(!openEyeConfirmPassword);
  };

  return (
    <form
      className="pt-4 mt-2 lg:pt-6 border-t border-theme-rule-medium"
      onSubmit={handleSubmit(onSubmit)}
    >
      <div>
        <label id={`${uuid}-usernameLabel`}>
          {t("AgentRegister-step2-userName")}
        </label>
        <input
          id={`${uuid}-username`}
          className="w-full"
          type="text"
          {...registrationStepTwoRegister("username", {
            required: t("AgentRegister-step2-userNameInput-requiredText"),
            validate: {
              minimumCharCount: (v) =>
                v.length >= 10 ||
                t("AgentRegister-step2-userNameInput-minimumCharCount"),
              maximumCharCount: (v) =>
                v.length <= 50 ||
                t("AgentRegister-step2-userNameInput-maximumCharCount"),
              illegalLetter: (v) =>
                v.replace(/[A-Za-z0-9\.\-\_]/g, "").length < 1 ||
                t("AgentRegister-step2-userNameInput-illegalLetter"),
            },
          })}
          aria-labelledby={`${uuid}-usernameLabel`}
        />

        {registrationStepTwoErrors.username && (
          <FormInputError
            id={`${uuid}-usernameError`}
            errorMessage={registrationStepTwoErrors.username.message}
          />
        )}
      </div>

      <div id="ID_passwordDesc" className="mt-4 text-sm">
        <div className="font-semibold">
          {t("AgentRegister-step2-userName-guide")}
        </div>
        <ul className="list-disc ml-4">
          <li>{t("AgentRegister-step2-userName-guide-1")}</li>
          <li>{t("AgentRegister-step2-userName-guide-2")}</li>
          <li>{t("AgentRegister-step2-userName-guide-3")}</li>
          <li>{t("AgentRegister-step2-userName-guide-4")}</li>
        </ul>
      </div>

      <div className="mt-4 relative">
        <label id={`${uuid}-passwordLabel`}>
          {t("AgentRegister-step2-password")}
        </label>
        <input
          id={`${uuid}-password`}
          className="w-full"
          type={openEyePassword ? "password" : "text"}
          autoComplete="off"
          {...registrationStepTwoRegister("password", {
            required: t("AgentRegister-step2-password-requiredText"),
            validate: {
              charCount: (v) =>
                (v.length >= 10 && v.length <= 61) ||
                t("AgentRegister-step2-password-charCountError"),
              hasLowerCaseLetter: (v) =>
                /^(?=.*[a-z])/.test(v) ||
                t("AgentRegister-step2-password-hasLowerCaseLetterError"),
              hasCapitalizedCaseLetter: (v) =>
                /^(?=.*[A-Z])/.test(v) ||
                t("AgentRegister-step2-password-hasCapitalizedCaseLetterError"),
              hasNumericLetter: (v) =>
                /^(?=.*[0-9])/.test(v) ||
                t("AgentRegister-step2-password-hasNumericLetterError"),
              allowedSpecialChars: (v) =>
                /^(?=.*[!@#\$%&\*\(\)])/.test(v) ||
                t("AgentRegister-step2-password-allowedSpecialCharsError"),
              illegalLetter: (v) =>
                v.replace(/[A-Za-z0-9!@#\$%&\*\(\)]/g, "").length < 1 ||
                t("AgentRegister-step2-password-illegalLetterError"),
            },
          })}
          aria-labelledby={`${uuid}-passwordLabel`}
        />
        <div className="absolute top-8 right-4">
          {openEyePassword ? (
            <AiOutlineEye onClick={togglePasswordEye}></AiOutlineEye>
          ) : (
            <AiOutlineEyeInvisible
              onClick={togglePasswordEye}
            ></AiOutlineEyeInvisible>
          )}
        </div>
        {registrationStepTwoErrors.password && (
          <FormInputError
            id={`${uuid}-passwordError`}
            errorMessage={registrationStepTwoErrors.password.message}
          />
        )}
      </div>

      <div id={`${uuid}-passwordDesc`} className="mt-4 text-sm">
        <div className="font-semibold">
          {t("AgentRegister-step2-password-guide")}
        </div>
        <ul className="list-disc ml-4">
          <li>{t("AgentRegister-step2-userName-guide-1")}</li>

          <li>{t("AgentRegister-step2-userName-guide-2")}</li>
          <li>{t("AgentRegister-step2-userName-guide-3")}</li>
          <li>{t("AgentRegister-step2-userName-guide-4")}</li>
        </ul>
      </div>

      <div className="mt-4 relative">
        <label id={`${uuid}-confirmPasswordLabel`}>
          {t("AgentRegister-step2-confirmPassword")}
        </label>
        <input
          id={`${uuid}-confirmPassword`}
          className="w-full"
          type={openEyeConfirmPassword ? "password" : "text"}
          {...registrationStepTwoRegister("confirmPassword", {
            required: t("AgentRegister-step2-confirmPassword-requiredText"),
            validate: (value) => {
              if (getValues("password") !== value)
                return t(
                  "AgentRegister-step2-confirmPassword-passwordMismatchError"
                );
            },
          })}
          aria-labelledby={`${uuid}-confirmPasswordLabel`}
        />
        <div className="absolute top-8 right-4">
          {openEyeConfirmPassword ? (
            <AiOutlineEye onClick={toggleConfirmPasswordEye}></AiOutlineEye>
          ) : (
            <AiOutlineEyeInvisible
              onClick={toggleConfirmPasswordEye}
            ></AiOutlineEyeInvisible>
          )}
        </div>
        {registrationStepTwoErrors.confirmPassword && (
          <FormInputError
            id={`${uuid}-confirmPasswordError`}
            errorMessage={registrationStepTwoErrors.confirmPassword.message}
          />
        )}
      </div>

      <div className="mt-4">
        <label id={`${uuid}-emailLabel`}>
          {t("AgentRegister-step2-email")}
        </label>
        <input
          disabled={agentRegistrationFormData.email !== "" ? true : false}
          id={`${uuid}-email`}
          className="w-full"
          type="text"
          {...registrationStepTwoRegister("email", {
            required: t("AgentRegister-step2-email-requiredText"),
            pattern: {
              value: /^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/,
              message: t("AgentRegister-step2-email-invalidEmailError"),
            },
            validate: (value) => {
              if (roleid !== 7) {
                let domainList = config.ALLOWED_EMAIL_DOMAINLIST.split(";");
                if (domainList.length > 0) {
                  if (
                    !domainList.some(
                      (x) =>
                        x.toLowerCase() === value.split("@")[1].toLowerCase()
                    )
                  )
                    return t("AgentRegister-step2-email-allowedDomainError");
                }
              }
            },
          })}
          aria-labelledby={`${uuid}-emailLabel`}
        />

        {registrationStepTwoErrors.email && (
          <FormInputError
            id={`${uuid}-emailError`}
            errorMessage={registrationStepTwoErrors.email.message}
          />
        )}
      </div>
      {agentRegistrationFormData.email === "" && (
        <div
          id={`${uuid}-ssnnote`}
          className="text-xxs text-theme-text-tertiary mt-1"
        >
          {t("AgentRegister-step2-email-errorText")}
        </div>
      )}

      <div className="mt-4">
        <label id={`${uuid}-confirmEmailLabel`}>
          {t("AgentRegister-step2-confirmEmail")}
        </label>
        <input
          disabled={agentRegistrationFormData.email !== "" ? true : false}
          id={`${uuid}-confirmEmail`}
          className="w-full"
          type="text"
          {...registrationStepTwoRegister("confirmEmail", {
            required: t("AgentRegister-step2-confirmEmail-requiredText"),
            validate: (value) => {
              if (getValues("email") !== value)
                return t("AgentRegister-step2-confirmEmail-emailMismatchError");
            },
          })}
          aria-labelledby={`${uuid}-confirmEmailLabel`}
        />

        {registrationStepTwoErrors.confirmEmail && (
          <FormInputError
            id={`${uuid}-confirmEmailError`}
            errorMessage={registrationStepTwoErrors.confirmEmail.message}
          />
        )}
      </div>

      <div className="mt-4 text-right">
        <button
          className="ui-btn-secondary ui-btn-min-w mt-2"
          type="button"
          onClick={() => setCurrentStep(1)}
        >
          {t("AgentRegister-step2-previousButton")}
          <span className="sr-only">
            {t("AgentRegister-step2-previousButton-srText")}
          </span>
        </button>
        <button className="ui-btn-primary ui-btn-min-w mt-2" type="submit">
          {t("AgentRegister-step2-submitButton")}
          <span className="sr-only">
            {t("AgentRegister-step2-submitButton-srText")}
          </span>
        </button>
      </div>
      <DomainErrorModal
        modalState={modalState}
        setModalState={setModalState}
        errorInfo={errorInfo}
      />
    </form>
  );
};

export default StepTwo;
